import Swiper from './swiper.min.js';

(() => {
  'use strict';

  if (window.__themeSlideshowComponentDefined) return;
  window.__themeSlideshowComponentDefined = true;

  const VIEWPORT_SELECTOR = '[data-slideshow-viewport]';
  const PREV_SELECTOR = '[data-slideshow-prev]';
  const NEXT_SELECTOR = '[data-slideshow-next]';
  const PAGINATION_SELECTOR = '[data-slideshow-pagination]';
  const CURRENT_SELECTOR = '[data-slideshow-current]';
  const TOTAL_SELECTOR = '[data-slideshow-total]';
  const PROGRESS_SELECTOR = '[data-slideshow-progress]';
  const AUTOPLAY_TOGGLE_SELECTOR = '[data-slideshow-autoplay-toggle]';
  const AUTOPLAY_LABEL_SELECTOR = '[data-slideshow-autoplay-label]';
  const SPLIT_CONTENT_SELECTOR = '[data-slideshow-split-content]';
  const SPLIT_GHOST_SELECTOR = '[data-slideshow-split-ghost]';
  const SPLIT_PROGRESS_LINE_SELECTOR = '[data-slideshow-split-progress-line]';
  const SPLIT_SOURCE_SELECTOR = '[data-split-source]';
  const SLIDE_SELECTOR = '.section-slideshow__slide';
  const PAGINATION_BULLET_SELECTOR = '.swiper-pagination-bullet';
  const PAGINATION_INDICATOR_CLASS = 'section-slideshow__pagination-indicator';
  const DRAGGING_CLASS = 'is-dragging';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button',
    'input',
    'select',
    'textarea',
    '[tabindex]',
    '[contenteditable="true"]',
  ].join(',');
  const ORIGINAL_TABINDEX_ATTR = 'data-slideshow-original-tabindex';

  const parseBoolean = (value) => String(value) === 'true';
  const parseInteger = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const lerp = (start, end, progress) => start + ((end - start) * progress);
  const easeOut = (progress, power = 2) => 1 - Math.pow(1 - progress, power);

  class MainSlideshow extends HTMLElement {
    constructor() {
      super();

      this.sectionRoot = null;
      this.viewport = null;
      this.prevButton = null;
      this.nextButton = null;
      this.pagination = null;
      this.counterCurrent = null;
      this.counterTotal = null;
      this.progressFill = null;
      this.autoplayToggle = null;
      this.autoplayLabel = null;
      this.splitContent = null;
      this.splitGhost = null;
      this.splitProgressLines = [];
      this.splitUpdateTimer = null;
      this.slides = [];
      this.swiper = null;
      this.paginationIndicator = null;
      this.lastPaginationRealIndex = null;
      this.paginationMoveRaf = null;
      this.paginationIndicatorX = 0;
      this.splitProgressRaf = null;
      this.prefersReducedMotion = false;
      this.reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isDocumentVisible = document.visibilityState !== 'hidden';
      this.currentRevealIndex = null;
      this.revealTimer = null;

      this.onSectionLoad = this.onSectionLoad.bind(this);
      this.onSectionUnload = this.onSectionUnload.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
      this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
      this.onAutoplayToggleClick = this.onAutoplayToggleClick.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
    }

    connectedCallback() {
      this.sectionRoot = this.closest('.shopify-section');
      this.viewport = this.querySelector(VIEWPORT_SELECTOR);
      this.prevButton = this.querySelector(PREV_SELECTOR);
      this.nextButton = this.querySelector(NEXT_SELECTOR);
      this.pagination = this.querySelector(PAGINATION_SELECTOR);
      this.counterCurrent = this.querySelector(CURRENT_SELECTOR);
      this.counterTotal = this.querySelector(TOTAL_SELECTOR);
      this.progressFill = this.querySelector(PROGRESS_SELECTOR);
      this.autoplayToggle = this.querySelector(AUTOPLAY_TOGGLE_SELECTOR);
      this.autoplayLabel = this.autoplayToggle?.querySelector(AUTOPLAY_LABEL_SELECTOR) || null;
      this.splitContent = this.querySelector(SPLIT_CONTENT_SELECTOR);
      this.splitGhost = this.querySelector(SPLIT_GHOST_SELECTOR);
      this.splitProgressLines = Array.from(this.querySelectorAll(SPLIT_PROGRESS_LINE_SELECTOR));
      this.slides = Array.from(this.querySelectorAll(SLIDE_SELECTOR));
      this.prefersReducedMotion = this.reducedMotionMedia.matches;

      document.addEventListener('shopify:section:load', this.onSectionLoad);
      document.addEventListener('shopify:section:unload', this.onSectionUnload);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (this.reducedMotionMedia.addEventListener) {
        this.reducedMotionMedia.addEventListener('change', this.onReducedMotionChange);
      } else if (this.reducedMotionMedia.addListener) {
        this.reducedMotionMedia.addListener(this.onReducedMotionChange);
      }

      if (this.autoplayToggle) {
        this.autoplayToggle.addEventListener('click', this.onAutoplayToggleClick);
      }

      this.initialize();
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);

      if (this.reducedMotionMedia.removeEventListener) {
        this.reducedMotionMedia.removeEventListener('change', this.onReducedMotionChange);
      } else if (this.reducedMotionMedia.removeListener) {
        this.reducedMotionMedia.removeListener(this.onReducedMotionChange);
      }

      if (this.autoplayToggle) {
        this.autoplayToggle.removeEventListener('click', this.onAutoplayToggleClick);
      }

      if (this.splitUpdateTimer) {
        window.clearTimeout(this.splitUpdateTimer);
        this.splitUpdateTimer = null;
      }
      this.clearRevealTimer();

      if (this.paginationMoveRaf) {
        window.cancelAnimationFrame(this.paginationMoveRaf);
        this.paginationMoveRaf = null;
      }

      if (this.splitProgressRaf) {
        window.cancelAnimationFrame(this.splitProgressRaf);
        this.splitProgressRaf = null;
      }

      this.destroySwiper();
    }

    get slideCount() {
      return this.slides.length;
    }

    get transitionStyle() {
      return (this.dataset.transitionStyle || 'slide').trim();
    }

    get navigationStyle() {
      return (this.dataset.navigationStyle || 'minimal').trim();
    }

    get loopEnabled() {
      const loopValue = this.dataset.loop;
      if (loopValue == null || loopValue === '') return true;
      return parseBoolean(loopValue);
    }

    get autoplayEnabled() {
      return parseBoolean(this.dataset.autoplay);
    }

    get autoplayDelay() {
      return parseInteger(this.dataset.autoplaySpeed, 5000);
    }

    get a11yPrevMessage() {
      return this.dataset.a11yPrev || 'Previous slide';
    }

    get a11yNextMessage() {
      return this.dataset.a11yNext || 'Next slide';
    }

    get a11yBulletMessage() {
      return this.dataset.a11yBullet || 'Go to slide {{index}}';
    }

    get autoplayPlayLabel() {
      return this.autoplayToggle?.dataset.labelPlay || 'Play';
    }

    get autoplayPauseLabel() {
      return this.autoplayToggle?.dataset.labelPause || 'Pause';
    }

    get isSplitLayout() {
      return (this.dataset.layoutStyle || '').trim() === 'split_editorial';
    }

    get motionSpeedSetting() {
      return (document.body?.dataset.motionSpeed || 'normal').trim();
    }

    get transitionDurationMs() {
      if (this.prefersReducedMotion) return 0;

      switch (this.motionSpeedSetting) {
        case 'express':
          return 580;
        case 'subtle':
          return 900;
        default:
          return 720;
      }
    }

    initialize() {
      this.slides = Array.from(this.querySelectorAll(SLIDE_SELECTOR));
      this.setCounterTotal(this.slideCount);
      this.prepareWordReveal();
      this.classList.add('is-word-reveal-ready');
      this.currentRevealIndex = null;
      this.updateActiveSlide(0, { animateSplitContent: false, forceReveal: true, revealDelayMs: 0 });

      if (!this.viewport || !Swiper || this.slideCount <= 1) {
        this.syncControlVisibility(false);
        this.syncAutoplayLabel();
        return;
      }

      this.initSwiper();
    }

    initSwiper() {
      const useFadeTransition = this.transitionStyle === 'fade';
      const shouldAutoplay = this.autoplayEnabled && !this.prefersReducedMotion;
      const showArrows = this.navigationStyle === 'minimal';
      const showDots = this.navigationStyle === 'dots';
      const enableLoop = this.loopEnabled && this.slideCount > 1;
      const transitionSpeed = this.transitionDurationMs;

      const options = {
        speed: transitionSpeed,
        rewind: !enableLoop,
        loop: enableLoop,
        loopAdditionalSlides: enableLoop ? 1 : 0,
        slidesPerView: 1,
        threshold: 4,
        roundLengths: false,
        watchSlidesProgress: false,
        resistanceRatio: 0.8,
        allowTouchMove: true,
        simulateTouch: true,
        followFinger: true,
        touchRatio: 1.05,
        longSwipes: true,
        longSwipesMs: 480,
        longSwipesRatio: 0.2,
        grabCursor: !this.prefersReducedMotion,
        watchOverflow: true,
        wrapperClass: 'section-slideshow__track',
        slideClass: 'section-slideshow__slide',
        a11y: {
          enabled: true,
          prevSlideMessage: this.a11yPrevMessage,
          nextSlideMessage: this.a11yNextMessage,
          paginationBulletMessage: this.a11yBulletMessage,
          notificationClass: 'visually-hidden'
        }
      };

      if (useFadeTransition) {
        options.effect = 'fade';
        options.fadeEffect = { crossFade: true };
      }

      if (showArrows && this.prevButton && this.nextButton) {
        options.navigation = {
          prevEl: this.prevButton,
          nextEl: this.nextButton
        };
      }

      if (showDots && this.pagination) {
        options.pagination = {
          el: this.pagination,
          clickable: true
        };
      }

      if (shouldAutoplay) {
        options.autoplay = {
          delay: this.autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        };
      }

      this.swiper = new Swiper(this.viewport, options);
      this.classList.add('is-swiper-active');
      this.syncControlVisibility(true);
      this.syncDuplicateSlideAccessibility();
      this.setupPaginationIndicator();

      const clearDraggingState = () => {
        this.classList.remove(DRAGGING_CLASS);
      };

      this.swiper.on('touchStart', () => {
        this.classList.add(DRAGGING_CLASS);
      });

      this.swiper.on('sliderFirstMove', () => {
        this.classList.add(DRAGGING_CLASS);
      });

      this.swiper.on('touchEnd', clearDraggingState);
      this.swiper.on('touchCancel', clearDraggingState);
      this.swiper.on('transitionEnd', clearDraggingState);

      this.swiper.on('slideChange', () => {
        const realIndex = this.swiper.realIndex ?? this.swiper.activeIndex ?? 0;
        this.updateActiveSlide(realIndex, { reveal: false });
        this.updatePaginationIndicator(realIndex);
        this.syncDuplicateSlideAccessibility();
      });

      this.swiper.on('slideChangeTransitionEnd', () => {
        const realIndex = this.swiper.realIndex ?? this.swiper.activeIndex ?? 0;
        this.updateActiveSlide(realIndex, {
          animateSplitContent: false,
          forceReveal: true,
          revealDelayMs: 0
        });
        this.updatePaginationIndicator(realIndex, { animate: false, restartLoader: false });
        this.syncDuplicateSlideAccessibility();
        window.requestAnimationFrame(() => this.syncDuplicateSlideAccessibility());
      });

      this.swiper.on('paginationUpdate', () => {
        this.setupPaginationIndicator();
        this.updatePaginationIndicator(this.swiper.realIndex ?? this.swiper.activeIndex ?? 0, { animate: false, restartLoader: false });
      });

      this.swiper.on('resize', () => {
        this.updatePaginationIndicator(this.swiper.realIndex ?? this.swiper.activeIndex ?? 0, { animate: false, restartLoader: false });
      });

      this.style.setProperty('--slideshow-slide-duration', `${transitionSpeed}ms`);
      this.style.setProperty('--slideshow-slide-speed', `${transitionSpeed}ms`);

      const initialIndex = this.swiper.realIndex ?? this.swiper.activeIndex ?? 0;
      this.updateActiveSlide(initialIndex, { forceReveal: true, revealDelayMs: 0 });
      this.updatePaginationIndicator(initialIndex, { animate: false });
      this.syncAutoplayLabel();

      window.requestAnimationFrame(() => {
        if (!this.swiper || !this.pagination) return;
        const refreshedIndex = this.swiper.realIndex ?? this.swiper.activeIndex ?? 0;
        this.setupPaginationIndicator();
        this.updatePaginationIndicator(refreshedIndex, { animate: false, restartLoader: false });
      });
    }

    destroySwiper() {
      this.classList.remove(DRAGGING_CLASS);
      this.clearRevealTimer();
      if (this.paginationMoveRaf) {
        window.cancelAnimationFrame(this.paginationMoveRaf);
        this.paginationMoveRaf = null;
      }
      if (this.splitProgressRaf) {
        window.cancelAnimationFrame(this.splitProgressRaf);
        this.splitProgressRaf = null;
      }

      if (!this.swiper) {
        this.syncControlVisibility(false);
        this.teardownPaginationIndicator();
        return;
      }

      this.swiper.destroy(true, true);
      this.swiper = null;
      this.classList.remove('is-swiper-active');
      this.classList.remove('is-autoplay-running');
      this.style.removeProperty('--slideshow-slide-duration');
      this.style.removeProperty('--slideshow-slide-speed');
      this.currentRevealIndex = null;
      this.querySelectorAll(SLIDE_SELECTOR).forEach((slide) => {
        if (slide instanceof HTMLElement) {
          slide.classList.remove('is-revealing');
        }
      });
      this.teardownPaginationIndicator();
      this.syncControlVisibility(false);
      this.updateActiveSlide(0, { animateSplitContent: false, forceReveal: true, revealDelayMs: 0 });
      this.resetProgress();
    }

    reinitialize() {
      this.destroySwiper();
      this.initialize();
    }

    syncControlVisibility(hasSlider) {
      const showArrows = hasSlider && this.navigationStyle === 'minimal';
      const showDots = hasSlider && this.navigationStyle === 'dots';
      const showAutoplayToggle = hasSlider && this.autoplayEnabled && !this.prefersReducedMotion;

      if (this.prevButton) this.prevButton.hidden = !showArrows;
      if (this.nextButton) this.nextButton.hidden = !showArrows;
      if (this.pagination) this.pagination.hidden = !showDots;
      if (this.autoplayToggle) this.autoplayToggle.hidden = !showAutoplayToggle;

      if (!showDots) {
        this.teardownPaginationIndicator();
      } else {
        this.setupPaginationIndicator();
      }
    }

    getPaginationBullets() {
      if (!this.pagination) return [];
      return Array.from(this.pagination.querySelectorAll(PAGINATION_BULLET_SELECTOR)).filter(
        (bullet) => bullet instanceof HTMLElement
      );
    }

    setupPaginationIndicator() {
      if (this.navigationStyle !== 'dots') return;
      if (!this.pagination) return;

      const bullets = this.getPaginationBullets();
      if (!bullets.length) return;

      if (!(this.paginationIndicator instanceof HTMLElement)) {
        const indicator = document.createElement('span');
        indicator.className = PAGINATION_INDICATOR_CLASS;
        indicator.setAttribute('aria-hidden', 'true');
        this.pagination.append(indicator);
        this.paginationIndicator = indicator;
        this.paginationIndicatorX = 0;
      }
    }

    teardownPaginationIndicator() {
      if (!(this.paginationIndicator instanceof HTMLElement)) return;
      if (this.paginationMoveRaf) {
        window.cancelAnimationFrame(this.paginationMoveRaf);
        this.paginationMoveRaf = null;
      }
      this.paginationIndicator.remove();
      this.paginationIndicator = null;
      this.paginationIndicatorX = 0;
      this.lastPaginationRealIndex = null;
    }

    updatePaginationIndicator(activeIndex, options = {}) {
      if (!(this.paginationIndicator instanceof HTMLElement)) {
        this.setupPaginationIndicator();
      }
      if (!(this.paginationIndicator instanceof HTMLElement)) return;

      const bullets = this.getPaginationBullets();
      if (!bullets.length) return;

      const normalizedIndex = Math.max(0, Math.min(activeIndex, bullets.length - 1));
      const targetBullet = bullets[normalizedIndex];
      if (!(targetBullet instanceof HTMLElement)) return;

      const indicatorWidth = this.paginationIndicator.offsetWidth || 18;
      const targetX = targetBullet.offsetLeft + ((targetBullet.offsetWidth - indicatorWidth) / 2);

      const animate = options.animate !== false && !this.prefersReducedMotion;
      const restartLoader = options.restartLoader !== false;
      const previousIndex = this.lastPaginationRealIndex;

      if (!animate || previousIndex == null || normalizedIndex === previousIndex) {
        if (this.paginationMoveRaf) {
          window.cancelAnimationFrame(this.paginationMoveRaf);
          this.paginationMoveRaf = null;
        }
        this.paginationIndicator.style.transform = `translateX(${targetX}px)`;
        this.paginationIndicatorX = targetX;
      } else {
        this.animatePaginationMove(targetX, 300);
      }

      this.lastPaginationRealIndex = normalizedIndex;
      this.syncPaginationAutoplayLoader(restartLoader);
    }

    animatePaginationMove(targetX, durationMs = 300) {
      if (!(this.paginationIndicator instanceof HTMLElement)) return;
      if (this.paginationMoveRaf) {
        window.cancelAnimationFrame(this.paginationMoveRaf);
        this.paginationMoveRaf = null;
      }

      const startX = Number.isFinite(this.paginationIndicatorX) ? this.paginationIndicatorX : targetX;
      if (this.prefersReducedMotion || durationMs <= 0 || Math.abs(startX - targetX) < 0.2) {
        this.paginationIndicator.style.transform = `translateX(${targetX}px)`;
        this.paginationIndicatorX = targetX;
        return;
      }

      let startTime = null;
      const run = (timestamp) => {
        if (!(this.paginationIndicator instanceof HTMLElement)) {
          this.paginationMoveRaf = null;
          return;
        }

        if (startTime == null) {
          startTime = timestamp;
        }

        const progress = Math.min((timestamp - startTime) / durationMs, 1);
        const eased = easeOut(progress, 2);
        const currentX = lerp(startX, targetX, eased);
        this.paginationIndicator.style.transform = `translateX(${currentX}px)`;
        this.paginationIndicatorX = currentX;

        if (progress < 1) {
          this.paginationMoveRaf = window.requestAnimationFrame(run);
          return;
        }

        this.paginationIndicator.style.transform = `translateX(${targetX}px)`;
        this.paginationIndicatorX = targetX;
        this.paginationMoveRaf = null;
      };

      this.paginationMoveRaf = window.requestAnimationFrame(run);
    }

    syncPaginationAutoplayLoader(restart = false) {
      if (!(this.paginationIndicator instanceof HTMLElement)) return;

      const loaderEnabled =
        this.navigationStyle === 'dots' &&
        this.autoplayEnabled &&
        !this.prefersReducedMotion;
      const loaderRunning = loaderEnabled && Boolean(this.swiper?.autoplay?.running);

      this.paginationIndicator.classList.toggle('is-autoplay-enabled', loaderEnabled);

      if (!loaderRunning) {
        this.paginationIndicator.classList.remove('is-autoplay-loading');
        return;
      }

      if (restart) {
        this.paginationIndicator.classList.remove('is-autoplay-loading');
        void this.paginationIndicator.offsetWidth;
      }

      this.paginationIndicator.classList.add('is-autoplay-loading');
    }

    setCounterTotal(totalSlides) {
      if (!this.counterTotal) return;
      this.counterTotal.textContent = String(totalSlides).padStart(2, '0');
    }

    updateActiveSlide(index, options = {}) {
      if (!this.slides.length) return;

      const animateSplitContent = options.animateSplitContent !== false;
      const forceReveal = options.forceReveal === true;
      const shouldReveal = options.reveal !== false;
      const revealDelayMs = Math.max(0, parseInteger(options.revealDelayMs, 0));
      const normalizedIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === normalizedIndex;
        slide.classList.toggle('is-active', isActive);
        this.syncSlideAccessibility(slide, isActive);
      });

      const activeSlide = this.slides[normalizedIndex] || null;
      const activeLabel = activeSlide?.dataset.slideDisplayIndex || String(normalizedIndex + 1).padStart(2, '0');

      if (this.counterCurrent) {
        this.counterCurrent.textContent = activeLabel;
      }

      if (this.progressFill) {
        const progress = ((normalizedIndex + 1) / this.slides.length) * 100;
        this.progressFill.style.inlineSize = `${progress}%`;
      }

      if (shouldReveal && (forceReveal || normalizedIndex !== this.currentRevealIndex)) {
        this.currentRevealIndex = normalizedIndex;
        this.scheduleReveal(normalizedIndex, revealDelayMs);
      }

      this.updateSplitPanel(activeSlide, activeLabel, animateSplitContent);
      this.updateSplitProgress(normalizedIndex);
    }

    clearRevealTimer() {
      if (!this.revealTimer) return;
      window.clearTimeout(this.revealTimer);
      this.revealTimer = null;
    }

    scheduleReveal(index, delayMs = 0) {
      this.clearRevealTimer();

      const resolvedDelay = this.prefersReducedMotion ? 0 : Math.max(0, delayMs);
      if (resolvedDelay === 0) {
        this.revealSlide(index);
        return;
      }

      this.revealTimer = window.setTimeout(() => {
        this.revealTimer = null;
        if (this.currentRevealIndex !== index) return;
        this.revealSlide(index);
      }, resolvedDelay);
    }

    revealSlide(index) {
      const allSlides = Array.from(this.querySelectorAll(SLIDE_SELECTOR));
      if (!allSlides.length) return;

      allSlides.forEach((slide) => {
        if (slide instanceof HTMLElement) {
          slide.classList.remove('is-revealing');
        }
      });

      const candidateSlides = allSlides.filter((slide) => {
        if (!(slide instanceof HTMLElement)) return false;
        return parseInteger(slide.dataset.slideIndex, -1) === index;
      });

      const activeSlide =
        candidateSlides.find((slide) => slide.classList.contains('swiper-slide-active')) ||
        candidateSlides[0] ||
        this.slides[index] ||
        null;

      if (!(activeSlide instanceof HTMLElement)) return;

      candidateSlides.forEach((slide) => {
        if (slide instanceof HTMLElement) {
          this.prepareWordReveal(slide);
        }
      });

      if (!this.classList.contains('is-word-reveal-ready')) {
        this.classList.add('is-word-reveal-ready');
      }

      const words = activeSlide.querySelectorAll('.section-slideshow-word');
      words.forEach((word, wordIndex) => {
        if (word instanceof HTMLElement) {
          word.style.setProperty('--ssw-index', String(wordIndex));
        }
      });

      if (this.prefersReducedMotion) {
        activeSlide.classList.add('is-revealing');
        return;
      }

      void activeSlide.offsetWidth;
      activeSlide.classList.add('is-revealing');
    }

    prepareWordReveal(scope = this) {
      if (!(scope instanceof HTMLElement)) return;

      const targets = scope.classList.contains('section-slideshow__slide')
        ? Array.from(scope.querySelectorAll('.section-slideshow__heading, .section-slideshow__description'))
        : Array.from(
            scope.querySelectorAll(
              '.section-slideshow__slide .section-slideshow__heading, .section-slideshow__slide .section-slideshow__description'
            )
          );

      targets.forEach((target) => {
        if (!(target instanceof HTMLElement)) return;
        if (target.closest(SPLIT_SOURCE_SELECTOR)) return;
        if (target.dataset.wordRevealPrepared === 'true') return;
        this.wrapWords(target);
        target.dataset.wordRevealPrepared = 'true';
      });
    }

    wrapWords(container) {
      if (!(container instanceof HTMLElement)) return;

      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          if (!(node instanceof Text)) return NodeFilter.FILTER_REJECT;
          if (!node.nodeValue || node.nodeValue.trim() === '') return NodeFilter.FILTER_REJECT;
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          if (parent.closest('.section-slideshow-word')) return NodeFilter.FILTER_REJECT;
          if (parent.closest('script, style, noscript')) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });

      const textNodes = [];
      while (walker.nextNode()) {
        textNodes.push(walker.currentNode);
      }

      textNodes.forEach((node) => {
        if (!(node instanceof Text) || !(node.parentNode instanceof Node)) return;

        const fragment = document.createDocumentFragment();
        const tokens = node.nodeValue.split(/(\s+)/);

        tokens.forEach((token) => {
          if (!token) return;

          if (/^\s+$/.test(token)) {
            fragment.appendChild(document.createTextNode(token));
            return;
          }

          const word = document.createElement('span');
          word.className = 'section-slideshow-word';
          word.textContent = token;
          fragment.appendChild(word);
        });

        node.parentNode.replaceChild(fragment, node);
      });
    }

    syncSlideAccessibility(slide, isActive) {
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      this.syncSlideInert(slide, !isActive);
      this.syncSlideFocusableDescendants(slide, !isActive);
    }

    syncDuplicateSlideAccessibility() {
      if (!this.viewport) return;

      this.viewport.querySelectorAll('.swiper-slide-duplicate').forEach((duplicateSlide) => {
        if (!(duplicateSlide instanceof HTMLElement)) return;
        const isVisibleActiveDuplicate = duplicateSlide.classList.contains('swiper-slide-active');
        duplicateSlide.setAttribute('aria-hidden', isVisibleActiveDuplicate ? 'false' : 'true');
        this.syncSlideInert(duplicateSlide, !isVisibleActiveDuplicate);
        this.syncSlideFocusableDescendants(duplicateSlide, !isVisibleActiveDuplicate);
      });
    }

    syncSlideInert(slide, shouldDisable) {
      if (!('inert' in slide)) return;
      slide.inert = shouldDisable;
    }

    syncSlideFocusableDescendants(slide, shouldDisable) {
      slide.querySelectorAll(FOCUSABLE_SELECTOR).forEach((node) => {
        if (!(node instanceof HTMLElement)) return;

        if (shouldDisable) {
          if (!node.hasAttribute(ORIGINAL_TABINDEX_ATTR)) {
            const tabindex = node.getAttribute('tabindex');
            if (tabindex === null) {
              node.setAttribute(ORIGINAL_TABINDEX_ATTR, '');
            } else {
              node.setAttribute(ORIGINAL_TABINDEX_ATTR, tabindex);
            }
          }
          node.setAttribute('tabindex', '-1');
          return;
        }

        if (!node.hasAttribute(ORIGINAL_TABINDEX_ATTR)) return;

        const originalTabindex = node.getAttribute(ORIGINAL_TABINDEX_ATTR);
        if (originalTabindex === '') {
          node.removeAttribute('tabindex');
        } else if (originalTabindex !== null) {
          node.setAttribute('tabindex', originalTabindex);
        }
        node.removeAttribute(ORIGINAL_TABINDEX_ATTR);
      });
    }

    updateSplitPanel(activeSlide, activeLabel, animateSplitContent = true) {
      if (!this.isSplitLayout || !this.splitContent) return;

      const source = activeSlide?.querySelector(SPLIT_SOURCE_SELECTOR);
      if (!source) return;

      const applyContent = () => {
        this.splitContent.innerHTML = source.innerHTML;
        if (this.splitGhost) {
          this.splitGhost.textContent = activeLabel;
        }
      };

      if (!animateSplitContent || this.prefersReducedMotion) {
        this.splitContent.classList.remove('is-updating');
        applyContent();
        return;
      }

      this.splitContent.classList.add('is-updating');
      if (this.splitUpdateTimer) {
        window.clearTimeout(this.splitUpdateTimer);
      }

      this.splitUpdateTimer = window.setTimeout(() => {
        applyContent();
        requestAnimationFrame(() => {
          this.splitContent.classList.remove('is-updating');
        });
        this.splitUpdateTimer = null;
      }, 175);
    }

    updateSplitProgress(activeIndex) {
      if (!this.isSplitLayout || !this.splitProgressLines.length) return;

      this.splitProgressLines.forEach((line, index) => {
        line.classList.remove('is-active', 'is-done');
        if (index < activeIndex) {
          line.classList.add('is-done');
        }
      });

      const currentLine = this.splitProgressLines[activeIndex];
      if (!currentLine) return;

      if (this.splitProgressRaf) {
        window.cancelAnimationFrame(this.splitProgressRaf);
      }
      this.splitProgressRaf = window.requestAnimationFrame(() => {
        currentLine.classList.add('is-active');
        this.splitProgressRaf = null;
      });
    }

    resetProgress() {
      if (!this.progressFill) return;
      this.progressFill.style.inlineSize = '';
    }

    onAutoplayToggleClick(event) {
      event.preventDefault();
      if (!this.swiper || !this.swiper.autoplay) return;

      if (this.swiper.autoplay.running) {
        this.swiper.autoplay.stop();
      } else {
        this.swiper.autoplay.start();
      }

      this.syncAutoplayLabel();
    }

    syncAutoplayLabel() {
      const isRunning = Boolean(this.swiper?.autoplay?.running);
      this.classList.toggle('is-autoplay-running', isRunning);
      this.syncPaginationAutoplayLoader(isRunning);

      if (!this.autoplayToggle || !this.autoplayLabel) return;
      if (!this.swiper || !this.swiper.autoplay) return;

      this.autoplayLabel.textContent = isRunning ? this.autoplayPauseLabel : this.autoplayPlayLabel;
      this.autoplayToggle.setAttribute('aria-pressed', isRunning ? 'false' : 'true');
    }

    onReducedMotionChange(event) {
      this.prefersReducedMotion = event.matches;
      this.reinitialize();
    }

    eventIsForThisSection(event) {
      if (!this.sectionRoot || !event?.target) return false;
      return this.sectionRoot.contains(event.target);
    }

    onSectionLoad(event) {
      if (!this.eventIsForThisSection(event)) return;
      this.reinitialize();
    }

    onSectionUnload(event) {
      if (!this.eventIsForThisSection(event)) return;
      this.destroySwiper();
    }

    onBlockSelect(event) {
      if (!this.eventIsForThisSection(event)) return;

      const slide = event.target.closest('[data-slide-index]');
      if (!slide) return;

      const targetIndex = parseInteger(slide.dataset.slideIndex, 0);
      if (this.swiper) {
        if (this.swiper.params?.loop && typeof this.swiper.slideToLoop === 'function') {
          this.swiper.slideToLoop(targetIndex, 0, false);
        } else {
          this.swiper.slideTo(targetIndex, 0, false);
        }
        this.updateActiveSlide(targetIndex, { animateSplitContent: false });
        if (this.swiper.autoplay?.running) {
          this.swiper.autoplay.stop();
        }
        this.syncAutoplayLabel();
        return;
      }

      slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }

    onBlockDeselect(event) {
      if (!this.eventIsForThisSection(event)) return;
      if (!this.swiper || !this.swiper.autoplay) return;
      if (!this.autoplayEnabled || this.prefersReducedMotion) return;
      if (!this.isDocumentVisible) return;

      if (!this.swiper.autoplay.running) {
        this.swiper.autoplay.start();
      }
      this.syncAutoplayLabel();
    }

    onVisibilityChange() {
      this.isDocumentVisible = document.visibilityState !== 'hidden';
      if (!this.swiper || !this.swiper.autoplay) return;

      if (!this.isDocumentVisible) {
        if (this.swiper.autoplay.running) {
          this.swiper.autoplay.stop();
        }
        this.syncAutoplayLabel();
        return;
      }

      if (!this.autoplayEnabled || this.prefersReducedMotion) return;
      if (!this.swiper.autoplay.running) {
        this.swiper.autoplay.start();
      }
      this.syncAutoplayLabel();
    }
  }

  if (!customElements.get('main-slideshow')) {
    customElements.define('main-slideshow', MainSlideshow);
  }
})();
